import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useBrandSelection } from './useBrandSelection';
import { usePageVisibility } from './usePageVisibility';

// Template brand ID that contains shared prompts for all brands
const TEMPLATE_BRAND_ID = '4fc6b2bd-dd18-4b38-9954-72a011af3313';

export interface ArtPrompt {
  id: string;
  brand_id: string;
  user_id?: string | null;
  name: string;
  prompt: string;
  category_tags: string[] | null;
  industry_tags: string[] | null;
  description?: string | null;
  usage_count: number | null;
  style_settings: any;
  type: 'image2image' | 'text2image' | 'image2video' | 'text2video' | 'morphing' | 'text2music' | 'text2speech';
  suggested_models: string[] | null;
  preview?: string | null;
  created_at: string | null;
  updated_at: string | null;
  isTemplate?: boolean; // Computed property indicating if this is a template prompt
}

export const useArtPrompts = () => {
  const [prompts, setPrompts] = useState<ArtPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { currentBrandId } = useBrandSelection();

  const fetchPrompts = async () => {
    if (!currentBrandId) return;

    setIsLoading(true);
    try {
      // If we're viewing the template brand, only fetch template prompts once
      if (currentBrandId === TEMPLATE_BRAND_ID) {
        const templateResponse = await supabase
          .from('os_art_prompts')
          .select('*')
          .eq('brand_id', TEMPLATE_BRAND_ID)
          .order('usage_count', { ascending: false });

        if (templateResponse.error) throw templateResponse.error;

        const templatePrompts = (templateResponse.data || []).map(prompt => ({
          ...prompt,
          isTemplate: true
        })) as ArtPrompt[];

        setPrompts(templatePrompts);
      } else {
        // For other brands, fetch both template and brand prompts
        const [templateResponse, brandResponse] = await Promise.all([
          supabase
            .from('os_art_prompts')
            .select('*')
            .eq('brand_id', TEMPLATE_BRAND_ID)
            .order('usage_count', { ascending: false }),
          supabase
            .from('os_art_prompts')
            .select('*')
            .eq('brand_id', currentBrandId)
            .order('usage_count', { ascending: false })
        ]);

        if (templateResponse.error) throw templateResponse.error;
        if (brandResponse.error) throw brandResponse.error;

        // Mark template prompts and combine with brand prompts
        const templatePrompts = (templateResponse.data || []).map(prompt => ({
          ...prompt,
          isTemplate: true
        })) as ArtPrompt[];

        const brandPrompts = (brandResponse.data || []).map(prompt => ({
          ...prompt,
          isTemplate: false
        })) as ArtPrompt[];

        // Combine and dedupe by id (prefer brand prompts over template)
        const allPrompts = [...templatePrompts];
        const templateIds = new Set(templatePrompts.map(p => p.id));
        
        brandPrompts.forEach(brandPrompt => {
          if (!templateIds.has(brandPrompt.id)) {
            allPrompts.push(brandPrompt);
          }
        });

        setPrompts(allPrompts);
      }
    } catch (error) {
      console.error('Error fetching art prompts:', error);
      toast.error('Failed to fetch art prompts');
    } finally {
      setIsLoading(false);
    }
  };

  const createPrompt = async (promptData: Omit<ArtPrompt, 'id' | 'created_at' | 'updated_at' | 'usage_count'>) => {
    if (!currentBrandId) return;

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('os_art_prompts')
        .insert({
          ...promptData,
          brand_id: currentBrandId,
          user_id: user?.id || null,
          usage_count: 0
        })
        .select()
        .single();

      if (error) throw error;
      
      // Don't add to state here - real-time subscription will handle it
      toast.success('Art prompt created successfully');
      return data;
    } catch (error) {
      console.error('Error creating art prompt:', error);
      toast.error('Failed to create art prompt');
      throw error;
    }
  };

  const updatePrompt = async (id: string, updates: Partial<ArtPrompt>) => {
    // Store the current prompt for potential rollback
    const currentPrompt = prompts.find(prompt => prompt.id === id);
    if (!currentPrompt) return;

    // Prevent updating template prompts unless we're in the template brand
    if (currentPrompt.isTemplate && currentBrandId !== TEMPLATE_BRAND_ID) {
      const error = new Error('Template prompts cannot be edited');
      toast.error('Template prompts cannot be edited');
      throw error;
    }

    // Optimistic update - immediately update UI
    setPrompts(prev => prev.map(prompt => 
      prompt.id === id ? { ...prompt, ...updates } : prompt
    ));

    try {
      const { data, error } = await supabase
        .from('os_art_prompts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update with server response to ensure consistency
      setPrompts(prev => prev.map(prompt => 
        prompt.id === id ? { ...prompt, ...(data as ArtPrompt), isTemplate: false } : prompt
      ));
      return data;
    } catch (error) {
      // Rollback optimistic update on error
      setPrompts(prev => prev.map(prompt => 
        prompt.id === id ? currentPrompt : prompt
      ));
      console.error('Error updating art prompt:', error);
      toast.error('Failed to update art prompt');
      throw error;
    }
  };

  const deletePrompt = async (id: string) => {
    // Find the prompt to check if it's a template
    const promptToDelete = prompts.find(prompt => prompt.id === id);
    if (!promptToDelete) return;

    // Prevent deleting template prompts unless we're in the template brand
    if (promptToDelete.isTemplate && currentBrandId !== TEMPLATE_BRAND_ID) {
      const error = new Error('Template prompts cannot be deleted');
      toast.error('Template prompts cannot be deleted');
      throw error;
    }

    try {
      const { error } = await supabase
        .from('os_art_prompts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPrompts(prev => prev.filter(prompt => prompt.id !== id));
      toast.success('Art prompt deleted successfully');
    } catch (error) {
      console.error('Error deleting art prompt:', error);
      toast.error('Failed to delete art prompt');
      throw error;
    }
  };

  const incrementUsage = async (id: string) => {
    try {
      // Get current usage count and increment it
      const currentPrompt = prompts.find(p => p.id === id);
      if (!currentPrompt) return;
      
      const newUsageCount = (currentPrompt.usage_count || 0) + 1;
      
      const { data, error } = await supabase
        .from('os_art_prompts')
        .update({ usage_count: newUsageCount })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setPrompts(prev => prev.map(prompt => 
        prompt.id === id ? { ...(data as ArtPrompt), isTemplate: prompt.isTemplate } : prompt
      ));
    } catch (error) {
      console.error('Error incrementing prompt usage:', error);
    }
  };

  const { isVisible } = usePageVisibility();

  useEffect(() => {
    fetchPrompts();
  }, [currentBrandId]);

  // Set up real-time subscription for the current brand (visibility-aware)
  useEffect(() => {
    if (!currentBrandId || !isVisible) return;

    const channel = supabase
      .channel(`art-prompts-changes-${currentBrandId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'os_art_prompts',
          filter: `brand_id=eq.${currentBrandId}`
        },
        (payload) => {
          console.log('New prompt inserted:', payload.new);
          const newPrompt = { ...payload.new, isTemplate: false } as ArtPrompt;
          setPrompts(prev => [newPrompt, ...prev]);
          toast.success('New prompt added!');
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'os_art_prompts',
          filter: `brand_id=eq.${currentBrandId}`
        },
        (payload) => {
          console.log('Prompt updated:', payload.new);
          const updatedPrompt = { ...payload.new, isTemplate: false } as ArtPrompt;
          setPrompts(prev => prev.map(prompt => 
            prompt.id === updatedPrompt.id ? updatedPrompt : prompt
          ));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'os_art_prompts',
          filter: `brand_id=eq.${currentBrandId}`
        },
        (payload) => {
          console.log('Prompt deleted:', payload.old);
          setPrompts(prev => prev.filter(prompt => prompt.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentBrandId, isVisible]);

  return {
    prompts,
    isLoading,
    fetchPrompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
    incrementUsage
  };
};